import { useState, useEffect, useCallback } from "react";
import React from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Trash2,
  TrendingUp,
  TrendingDown,
  Eye,
  Target,
  ArrowUpDown,
  Filter,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  X,
  EyeOff,
  CheckSquare,
  Square,
  MinusSquare,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const GSCTrackingPage = () => {
  const { sessionToken } = useAuth();
  const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
  
  // State
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [gscStatus, setGscStatus] = useState(null);
  const [allQueries, setAllQueries] = useState([]);
  const [excludedTerms, setExcludedTerms] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "position", direction: "asc" });
  const [showExcluded, setShowExcluded] = useState(false);
  const [savingExclusions, setSavingExclusions] = useState(false);
  const [showStats, setShowStats] = useState(true); // Platform owner controlled
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 250;
  
  // Bulk selection state
  const [selectedQueries, setSelectedQueries] = useState(new Set());
  
  // Manual exclusion dialog
  const [showManualExclude, setShowManualExclude] = useState(false);
  const [manualExcludeTerm, setManualExcludeTerm] = useState("");

  // Filter queries: ONLY show top 10 (position < 11) and apply search/exclusion filters
  // EXACT matching for exclusions - only exclude keywords that exactly match excluded terms
  const excludedTermsLowerSet = new Set(excludedTerms.map(t => t.toLowerCase()));
  const processedQueries = allQueries
    .filter(q => {
      // ONLY show top 10 keywords (position < 11)
      if (q.position >= 11) return false;
      
      // Search filter (this one should still be partial match for search functionality)
      if (searchFilter && !q.query.toLowerCase().includes(searchFilter.toLowerCase())) {
        return false;
      }
      // Show/hide excluded - EXACT match only
      const isExcluded = excludedTermsLowerSet.has(q.query.toLowerCase());
      if (showExcluded) return isExcluded;
      return !isExcluded;
    })
    .sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      return sortConfig.direction === "desc" ? bVal - aVal : aVal - bVal;
    });

  // Pagination calculations
  const totalPages = Math.ceil(processedQueries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedQueries = processedQueries.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, showExcluded, sortConfig]);

  // Stats for top 10 keywords (position < 11, excluding excluded terms)
  const excludedTermsLower = excludedTerms.map(t => t.toLowerCase());
  const top10Queries = allQueries.filter(q => 
    q.position < 11 && !excludedTermsLower.includes(q.query.toLowerCase())
  );
  
  // Stats for ALL keywords in the period (excluding excluded terms, but ANY position)
  // These reflect total GSC performance, not just top 10
  const allNonExcludedQueries = allQueries.filter(q => 
    !excludedTermsLower.includes(q.query.toLowerCase())
  );
  const totalImpressions = allNonExcludedQueries.reduce((sum, q) => sum + q.impressions, 0);
  
  // Average position is for top 10 only (makes sense contextually)
  const avgPosition = top10Queries.length > 0
    ? top10Queries.reduce((sum, q) => sum + q.position, 0) / top10Queries.length
    : 0;

  // Calculate rolling 90-day date range for keyword monitoring
  const getRolling90DayDates = () => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { start, end };
  };

  // Fetch GSC status and data - always uses rolling 90-day window
  const fetchGSCData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      // Get status
      const statusRes = await axios.get(`${API}/gsc/status`, { params });
      setGscStatus(statusRes.data);
      setExcludedTerms(statusRes.data.excluded_terms || []);
      setShowStats(statusRes.data.show_stats !== false);
      
      // Get queries only if connected - always use rolling 90-day window
      if (statusRes.data.connected) {
        try {
          const dates = getRolling90DayDates();
          console.log(`GSC: Fetching 90-day data: ${dates.start} to ${dates.end}`);
          
          // No limit - fetch ALL keywords with pagination
          const queriesRes = await axios.get(`${API}/gsc/top-queries`, { 
            params: { 
              ...params, 
              start_date: dates.start,
              end_date: dates.end
            } 
          });
          
          console.log(`GSC: Received ${queriesRes.data.queries?.length || 0} queries, from_cache=${queriesRes.data.from_cache}`);
          setAllQueries(queriesRes.data.queries || []);
        } catch (err) {
          console.error("Error fetching GSC queries:", err);
          toast.error("Failed to load keyword data");
          setAllQueries([]);
        }
      }
    } catch (error) {
      console.error("Error fetching GSC data:", error);
      setGscStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [sessionToken, currentTenantId]);

  useEffect(() => {
    fetchGSCData();
  }, [fetchGSCData]);

  // Connect GSC via OAuth
  const handleConnectGSC = async () => {
    if (!currentTenantId) {
      toast.error("No tenant selected");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/gsc/connect`, {
        params: {
          tenant_id: currentTenantId,
          origin: window.location.origin
        }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error("Error initiating GSC OAuth:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate GSC connection");
    }
  };

  // Sync GSC data
  const handleSync = async () => {
    setSyncing(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      await axios.post(`${API}/gsc/sync`, {}, { params });
      toast.success("GSC data sync triggered");
      setTimeout(() => fetchGSCData(), 2000);
    } catch (error) {
      toast.error("Failed to sync GSC data");
    } finally {
      setSyncing(false);
    }
  };

  // Save exclusions to backend
  const saveExclusions = async (newExcludedTerms) => {
    setSavingExclusions(true);
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) params["X-Tenant-ID"] = currentTenantId;
      
      await axios.put(`${API}/gsc/excluded-terms`, { 
        terms: newExcludedTerms 
      }, { params });
      
      setExcludedTerms(newExcludedTerms);
      toast.success("Exclusions saved");
    } catch (error) {
      toast.error("Failed to save exclusions");
    } finally {
      setSavingExclusions(false);
    }
  };

  // Toggle single query exclusion
  const toggleExclusion = (query) => {
    const isCurrentlyExcluded = excludedTerms.includes(query);
    const newExcludedTerms = isCurrentlyExcluded
      ? excludedTerms.filter(t => t !== query)
      : [...excludedTerms, query];
    saveExclusions(newExcludedTerms);
  };

  // Bulk select helpers
  const handleSelectAll = () => {
    if (selectedQueries.size === processedQueries.length) {
      setSelectedQueries(new Set());
    } else {
      setSelectedQueries(new Set(processedQueries.map(q => q.query)));
    }
  };

  const handleSelectQuery = (query) => {
    const newSelected = new Set(selectedQueries);
    if (newSelected.has(query)) {
      newSelected.delete(query);
    } else {
      newSelected.add(query);
    }
    setSelectedQueries(newSelected);
  };

  // Bulk actions
  const handleBulkExclude = () => {
    const newExcludedTerms = [...new Set([...excludedTerms, ...selectedQueries])];
    saveExclusions(newExcludedTerms);
    setSelectedQueries(new Set());
  };

  const handleBulkInclude = () => {
    const newExcludedTerms = excludedTerms.filter(t => !selectedQueries.has(t));
    saveExclusions(newExcludedTerms);
    setSelectedQueries(new Set());
  };

  // Manual exclusion
  const handleAddManualExclusion = () => {
    if (!manualExcludeTerm.trim()) return;
    const newExcludedTerms = [...new Set([...excludedTerms, manualExcludeTerm.trim()])];
    saveExclusions(newExcludedTerms);
    setManualExcludeTerm("");
    setShowManualExclude(false);
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  // Selection state indicator
  const allSelected = processedQueries.length > 0 && selectedQueries.size === processedQueries.length;
  const someSelected = selectedQueries.size > 0 && selectedQueries.size < processedQueries.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  // Show connection required screen if GSC is not connected
  if (!gscStatus?.connected) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="gsc-tracking-page">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Search className="w-10 h-10 text-orange-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Connect Google Search Console</h1>
          <p className="text-slate-500 max-w-md mb-6">
            Connect your Google Search Console account to track keyword rankings, 
            monitor organic search performance, and manage keyword exclusions.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={handleConnectGSC}
              className="gap-2 bg-orange-600 hover:bg-orange-700"
              data-testid="connect-gsc-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Connect Google Search Console
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              Go Back
            </Button>
          </div>
          <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200 max-w-md">
            <p className="text-sm text-slate-600">
              <strong>What you&apos;ll get:</strong>
            </p>
            <ul className="text-sm text-slate-500 mt-2 space-y-1 text-left">
              <li>• Top keyword rankings (position 1-10)</li>
              <li>• Click and impression data</li>
              <li>• CTR and position trends</li>
              <li>• Keyword exclusion management</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gsc-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Keyword Rankings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Google Search Console
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {gscStatus?.connected && (
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              Sync
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards - Only show keyword count always, impressions based on showStats */}
      <div className={`grid gap-4 ${showStats ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
        {/* Keywords count - always shown */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <Search className="w-4 h-4" />
              Top 10 Keywords
            </div>
            <p className="text-2xl font-bold text-slate-900">{top10Queries.length}</p>
            <p className="text-xs text-slate-400 mt-1">Position 1-10</p>
          </CardContent>
        </Card>
        
        {/* Average Position - always shown */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              Avg Position
            </div>
            <p className="text-2xl font-bold text-slate-900">{avgPosition.toFixed(1)}</p>
          </CardContent>
        </Card>
        
        {/* Impressions - controlled by showStats */}
        {showStats && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                <Eye className="w-4 h-4" />
                Impressions
              </div>
              <p className="text-2xl font-bold text-slate-900">{totalImpressions.toLocaleString()}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters & Bulk Actions Bar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search keywords..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="keyword-search"
                />
              </div>
              
              {/* Toggle Excluded View */}
              <Button
                variant={showExcluded ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setShowExcluded(!showExcluded);
                  setSelectedQueries(new Set());
                }}
                className="gap-2"
              >
                <EyeOff className="w-4 h-4" />
                {showExcluded ? "Showing Excluded" : "Show Excluded"}
                {excludedTerms.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{excludedTerms.length}</Badge>
                )}
              </Button>

              {/* Add Manual Exclusion */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManualExclude(true)}
                className="gap-2"
              >
                <Filter className="w-4 h-4" />
                Add Exclusion
              </Button>
            </div>

            {/* Bulk Actions */}
            {selectedQueries.size > 0 && (
              <div className="flex items-center gap-2 bg-orange-50 px-3 py-2 rounded-lg border border-orange-200">
                <span className="text-sm font-medium text-orange-800">
                  {selectedQueries.size} selected
                </span>
                {!showExcluded ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkExclude}
                    disabled={savingExclusions}
                    className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <EyeOff className="w-3 h-3" />
                    Exclude Selected
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkInclude}
                    disabled={savingExclusions}
                    className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Include Selected
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedQueries(new Set())}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Keywords Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">
                {showExcluded ? "Excluded Keywords" : "Top Keywords"}
              </CardTitle>
              <CardDescription>
                {showExcluded 
                  ? `${processedQueries.length} keywords excluded from reports`
                  : `${processedQueries.length} keywords tracked • Position ≤ 10`
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">
                    <button
                      onClick={handleSelectAll}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid="select-all-checkbox"
                    >
                      {allSelected ? (
                        <CheckSquare className="w-5 h-5 text-orange-600" />
                      ) : someSelected ? (
                        <MinusSquare className="w-5 h-5 text-orange-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="min-w-[300px]">Keyword</TableHead>
                  {showStats && (
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("clicks")}
                    >
                      <div className="flex items-center gap-1">
                        Clicks
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                  )}
                  {showStats && (
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("impressions")}
                    >
                      <div className="flex items-center gap-1">
                        Impressions
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                  )}
                  {showStats && (
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("ctr")}
                    >
                      <div className="flex items-center gap-1">
                        CTR
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                  )}
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort("position")}
                  >
                    <div className="flex items-center gap-1">
                      Position
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </TableHead>
                  <TableHead className="w-24 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedQueries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showStats ? 7 : 4} className="text-center py-8 text-slate-500">
                      {showExcluded 
                        ? "No excluded keywords" 
                        : searchFilter 
                          ? "No keywords match your search"
                          : "No keywords found"
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedQueries.map((q, idx) => {
                    const isSelected = selectedQueries.has(q.query);
                    const isExcluded = excludedTerms.includes(q.query);
                    const actualIdx = startIndex + idx;
                    
                    return (
                      <TableRow 
                        key={q.query} 
                        className={`${isSelected ? "bg-orange-50" : ""} ${isExcluded && !showExcluded ? "opacity-50" : ""}`}
                        data-testid={`keyword-row-${actualIdx}`}
                      >
                        <TableCell>
                          <button
                            onClick={() => handleSelectQuery(q.query)}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-orange-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{q.query}</span>
                            {q.position <= 3 && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                Top 3
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {showStats && <TableCell className="font-medium">{q.clicks.toLocaleString()}</TableCell>}
                        {showStats && <TableCell className="text-slate-600">{q.impressions.toLocaleString()}</TableCell>}
                        {showStats && (
                          <TableCell>
                            <span className={q.ctr >= 5 ? "text-green-600 font-medium" : "text-slate-600"}>
                              {q.ctr.toFixed(2)}%
                            </span>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {q.position <= 3 ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : q.position >= 8 ? (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            ) : null}
                            <span className={
                              q.position <= 3 ? "text-green-600 font-medium" : 
                              q.position >= 8 ? "text-red-600" : "text-slate-600"
                            }>
                              {q.position.toFixed(1)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExclusion(q.query)}
                            disabled={savingExclusions}
                            className={showExcluded 
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50" 
                              : "text-slate-500 hover:text-red-600 hover:bg-red-50"
                            }
                          >
                            {showExcluded ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              <EyeOff className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-slate-500">
                Showing {startIndex + 1}-{Math.min(endIndex, processedQueries.length)} of {processedQueries.length} keywords
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="hidden sm:flex"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm font-medium">Page {currentPage}</span>
                  <span className="text-sm text-slate-400">of {totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="hidden sm:flex"
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Exclusion Dialog */}
      <Dialog open={showManualExclude} onOpenChange={setShowManualExclude}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Exclusion</DialogTitle>
            <DialogDescription>
              Enter a keyword or phrase to exclude. Any query containing this term will be hidden from reports.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g., brand name, competitor, etc."
              value={manualExcludeTerm}
              onChange={(e) => setManualExcludeTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddManualExclusion()}
              data-testid="manual-exclusion-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualExclude(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddManualExclusion}
              disabled={!manualExcludeTerm.trim() || savingExclusions}
            >
              Add Exclusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Current Exclusions Summary */}
      {excludedTerms.length > 0 && !showExcluded && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Filter className="w-5 h-5 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-slate-700 mb-2">
                  {excludedTerms.length} Excluded Term{excludedTerms.length !== 1 ? "s" : ""}
                </p>
                <div className="flex flex-wrap gap-2">
                  {excludedTerms.map((term) => (
                    <Badge 
                      key={term} 
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-slate-200"
                      onClick={() => toggleExclusion(term)}
                    >
                      {term}
                      <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowExcluded(true)}
                className="text-slate-500"
              >
                View All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GSCTrackingPage;
